import type { UpdateContactRequest, Visibility } from "../api/types";

/**
 * The editable subset of a saved-freelancer record the user can change from the
 * popover / sheet panel (notes + visibility). Everything else on the entry is
 * server-owned and never sent in a PATCH.
 */
export type PatchDraft = {
  notes: string | null;
  visibility: Visibility;
};

/**
 * Notes empty-vs-null semantics (documented decision):
 * an empty string ("") and `null` both mean "no note" and are treated as
 * EQUIVALENT. Any non-empty string — including whitespace-only — is preserved
 * verbatim as content. Consequences:
 *   - toggling a field between "" and null issues NO PATCH (they are equal);
 *   - clearing a real note down to "" (or null) emits `{ notes: null }`.
 * The BE clears the note when it receives `notes: null`.
 */
const normalizeNotes = (notes: string | null): string | null =>
  notes === "" ? null : notes;

/**
 * Builds the minimal PATCH body for a saved-freelancer update by diffing the
 * last-persisted values (`initial`) against the current draft (`current`).
 *
 * Returns `null` when nothing the user can edit actually changed — the
 * blind-PATCH guard. This is what protects the post-409 window: when the
 * server's real notes are unknown the form pre-fills with the optimistic
 * placeholder, so if the user touches nothing `initial === current` and no
 * request is issued (a blind PATCH would otherwise wipe the server note).
 *
 * When something changed, returns ONLY the changed fields.
 */
export function buildPatchPayload(
  initial: PatchDraft,
  current: PatchDraft,
): UpdateContactRequest | null {
  const payload: UpdateContactRequest = {};

  const initialNotes = normalizeNotes(initial.notes);
  const currentNotes = normalizeNotes(current.notes);
  if (currentNotes !== initialNotes) {
    payload.notes = currentNotes;
  }

  if (current.visibility !== initial.visibility) {
    payload.visibility = current.visibility;
  }

  return Object.keys(payload).length === 0 ? null : payload;
}
